import { getFreshToken } from '@/utils/auth-token-provider';
import { convertPythonDictToJSON } from '@/utils/convert-python-dict';

// Chat WebSocket messages follow the NodeEvent format
export interface ChatMessage {
  // Core fields (similar to NodeEvent)
  node_id?: string;
  node_display_name?: string;
  event_type?: string; // 'processing', 'execution_successful', etc.
  display_type?: string; // 'chat_agent_message', 'thinking', etc.
  dynamic_message?: string;
  data?: string | object; // The actual message content
  mode?: string; // 'block', 'textstream'
  timestamp?: string;
  event_id?: string;
  block_id?: string;

  // Legacy/simple format fields (for backwards compatibility)
  message_type?: 'chat_message' | 'end_chat' | 'ping';
  message?: string;
  request_id?: string;
  metadata?: object;

  [key: string]: unknown;
}

export interface ChatConnectionState {
  isConnected: boolean;
}

type ConnectionStateListener = (state: ChatConnectionState) => void;

class ChatWebSocketService {
  private ws: WebSocket | null = null;
  private messageListeners: Map<string, Set<(message: ChatMessage) => void>> =
    new Map();
  private connectionStateListeners: Set<ConnectionStateListener> = new Set();

  /** Get current connection state */
  getConnectionState(): ChatConnectionState {
    return {
      isConnected: this.isConnected(),
    };
  }

  /** Subscribe to connection state changes */
  onConnectionStateChange(listener: ConnectionStateListener): () => void {
    this.connectionStateListeners.add(listener);
    // Immediately notify with current state
    listener(this.getConnectionState());
    return () => {
      this.connectionStateListeners.delete(listener);
    };
  }

  private notifyConnectionStateChange(): void {
    const state = this.getConnectionState();
    this.connectionStateListeners.forEach((listener) => listener(state));
  }

  private async buildUrl(chatSocketUrl: string): Promise<string> {
    const token = await getFreshToken();
    if (!token) {
      return chatSocketUrl;
    }

    try {
      const url = new URL(chatSocketUrl);
      url.searchParams.set('token', token);
      return url.toString();
    } catch (error) {
      console.error('Error building chat WebSocket URL:', error);
      return chatSocketUrl;
    }
  }

  async connect(chatSocketUrl: string): Promise<void> {
    // If already connected to same URL, resolve immediately
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.notifyConnectionStateChange();
      return;
    }

    // Close existing connection if any
    if (this.ws) {
      this.ws.close();
    }

    const urlWithToken = await this.buildUrl(chatSocketUrl);

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(urlWithToken);

        this.ws.onopen = () => {
          console.log('Chat WebSocket connected');
          this.notifyConnectionStateChange();
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            // Handle empty messages
            if (!event.data || event.data.trim() === '') {
              console.warn('Received empty chat WebSocket message');
              return;
            }

            // Preprocess the message to handle single quotes (Python-style JSON)
            let processedData = event.data;

            // Only convert if the message starts with single quote (Python dict style)
            // Valid JSON messages may contain single quotes in their content
            if (processedData.trim().startsWith("'")) {
              processedData = convertPythonDictToJSON(processedData);
            }

            const data: ChatMessage = JSON.parse(processedData);
            this.handleMessage(data);
          } catch (error) {
            console.error('Failed to parse chat message:', error);
            console.error('Raw message that failed to parse:', event.data);
          }
        };

        this.ws.onclose = (event) => {
          console.log('Chat WebSocket disconnected:', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
          });
          this.notifyConnectionStateChange();
        };

        this.ws.onerror = (error) => {
          console.error('Chat WebSocket error:', error);
          this.notifyConnectionStateChange();
          reject(error);
        };
      } catch (error) {
        this.notifyConnectionStateChange();
        reject(error);
      }
    });
  }

  private handleMessage(message: ChatMessage): void {
    // Emit to listeners for this specific message type
    const messageType = message.message_type;
    if (messageType) {
      const typeListeners = this.messageListeners.get(messageType);
      if (typeListeners) {
        typeListeners.forEach((listener) => listener(message));
      }
    }

    // Also emit to general listeners (wildcard)
    // This ensures all messages reach wildcard subscribers
    const generalListeners = this.messageListeners.get('*');
    if (generalListeners) {
      generalListeners.forEach((listener) => listener(message));
    }
  }

  /**
   * Subscribe to chat messages
   * @param messageType - The message type to subscribe to ('chat_message', 'end_chat', 'ping', or '*' for all)
   * @param callback - The callback to invoke when a message is received
   * @returns Unsubscribe function
   */
  subscribe(
    messageType: string,
    callback: (message: ChatMessage) => void,
  ): () => void {
    if (!this.messageListeners.has(messageType)) {
      this.messageListeners.set(messageType, new Set());
    }

    const listeners = this.messageListeners.get(messageType)!;
    listeners.add(callback);

    return () => {
      listeners.delete(callback);
      if (listeners.size === 0) {
        this.messageListeners.delete(messageType);
      }
    };
  }

  /**
   * Subscribe to all chat messages (shorthand for subscribe('*', callback))
   */
  subscribeToAll(callback: (message: ChatMessage) => void): () => void {
    return this.subscribe('*', callback);
  }

  send(message: string | object): void {
    console.log('Chat WebSocket send called, readyState:', this.ws?.readyState);
    if (this.ws?.readyState !== WebSocket.OPEN) {
      console.error(
        'Chat WebSocket is not connected, readyState:',
        this.ws?.readyState,
      );
      return;
    }
    const messageToSend =
      typeof message === 'string' ? message : JSON.stringify(message);
    this.ws.send(messageToSend);
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.notifyConnectionStateChange();
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

export const chatWebsocketService = new ChatWebSocketService();
